import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { faRedo, faTrashCan, faSearch } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import headerFooterApi from '../../api/headerFooterApi';

const TrashedHeaderFooter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(10);

  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isPermanentDeleteModalOpen, setIsPermanentDeleteModalOpen] = useState(false);
  const [codeToActOnId, setCodeToActOnId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const [lastPage, setLastPage] = useState(1);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchTrashedCodes = useCallback(async (page, perPage, search) => {
    setLoading(true);
    setError(null);
    try {
      const response = await headerFooterApi.getHeaderFooters({ page, perPage, search, includeTrashed: true });
      setCodes(response.data);
      setLastPage(response.pagination.last_page);
    } catch (err) {
      console.error('Failed to fetch trashed header/footers:', err);
      setError('Failed to load trashed header/footers. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrashedCodes(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [fetchTrashedCodes, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleRestoreClick = (id) => {
    setCodeToActOnId(id);
    setIsRestoreModalOpen(true);
  };

  const handleConfirmRestore = async () => {
    setIsRestoreModalOpen(false);
    if (codeToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await headerFooterApi.restoreHeaderFooter(codeToActOnId);
        await fetchTrashedCodes(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to restore code snippet:', err);
        setError('Failed to restore code snippet. Please try again.');
      } finally {
        setLoading(false);
        setCodeToActOnId(null);
      }
    }
  };

  const handlePermanentDeleteClick = (id) => {
    setCodeToActOnId(id);
    setIsPermanentDeleteModalOpen(true);
  };

  const handleConfirmPermanentDelete = async () => {
    setIsPermanentDeleteModalOpen(false);
    if (codeToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await headerFooterApi.permanentDeleteHeaderFooter(codeToActOnId);
        await fetchTrashedCodes(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to permanently delete code snippet:', err);
        setError('Failed to permanently delete code snippet. Please try again.');
      } finally {
        setLoading(false);
        setCodeToActOnId(null);
      }
    }
  };

  const handleCancelAction = () => {
    setIsRestoreModalOpen(false);
    setIsPermanentDeleteModalOpen(false);
    setCodeToActOnId(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-text-white">Trashed Header & Footer Codes</h1>
        <Button
          onClick={() => navigate('/header-footer')}
          className="bg-accent-red hover:bg-red-700 text-white"
        >
          Back to Header & Footer
        </Button>
      </div>

      <div className="mb-4">
        <TextInput
          label="Search Trashed Codes"
          id="search-codes"
          name="search-codes"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by name or position..."
          icon={faSearch}
        />
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={itemsPerPage} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Name</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Position</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.length > 0 ? (
                  codes.map((code) => (
                    <tr key={code.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{code.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{code.name}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{code.position}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm flex items-center flex-nowrap">
                        <Button
                          onClick={() => handleRestoreClick(code.id)}
                          className="text-green-500 hover:text-green-600 mr-3"
                          icon={faRedo}
                          tooltipId={`restore-code-tooltip-${code.id}`}
                          tooltipContent="Restore"
                        />
                        <Button
                          onClick={() => handlePermanentDeleteClick(code.id)}
                          className="text-red-600 hover:text-red-700"
                          icon={faTrashCan}
                          tooltipId={`permanent-delete-code-tooltip-${code.id}`}
                          tooltipContent="Permanent Delete"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="py-3 px-4 text-center text-sm text-text-gray">No trashed code snippets found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
          />
        </>
      )}

      <ConfirmationModal
        isOpen={isRestoreModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmRestore}
        title="Confirm Restore"
        message="Are you sure you want to restore this code snippet?"
      />

      <ConfirmationModal
        isOpen={isPermanentDeleteModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmPermanentDelete}
        title="Confirm Permanent Delete"
        message="Are you sure you want to permanently delete this code snippet? This action cannot be undone."
      />
    </div>
  );
};

export default TrashedHeaderFooter;